<template>
  <v-container>
    <v-row>
      <v-col>
        <FileUploader
          :save-activated="userClickedSave"
          :edited-object-id="docketEntry.id"
          for-data-type="docketEntries"
          :existing-files="docketEntry.files"
          :file-previews="filePreviews"
          :sharepoint-files="existingSharepointFiles"
          @is-valid-update="updateFileUploadValid"
          @files-updated="updateFilesAreSelected"
          @on-prepare="saveEntry"
          @preview-fetched="emitFilePreviewUpdate"
          @sharepoint-file-deleted="onSharepointFileDeleted"
        >
          <template #additional-sources>
            <OneDriveFileInput
              ref="oneDriveInput"
              @file-pending="onOneDriveFilePending"
              @file-validated="onOneDriveFileValidated"
            />
          </template>
        </FileUploader>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'

import FileUploader from '@/components/Misc/FileUploader'
import OneDriveFileInput from './OneDriveFileInput.vue'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'

export default {
  name: 'DocketEntryEditorAttachments',

  components: {
    FileUploader,
    OneDriveFileInput,
  },

  props: {
    saveActivated: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      filePreviews: [],
      userClickedSave: false,
    }
  },

  computed: {
    docketEntry () {
      return this.editedDocketEntry
    },

    existingSharepointFiles () {
      return this.docketEntry?.docket_entry_sharepoint_files || []
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
    ]),
  },

  watch: {
    saveActivated (val) {
      this.userClickedSave = val
    },
  },

  methods: {
    emitFilePreviewUpdate (filePreview) {
      this.$emit('preview-fetched', filePreview)
    },

    onOneDriveFilePending (pendingData) {
      if (!this.docketEntry.docket_entry_sharepoint_files) {
        this.docketEntry.docket_entry_sharepoint_files = []
      }
      this.docketEntry.docket_entry_sharepoint_files.push({
        fetchStatus: pendingData.fetchStatus,
        file_name: pendingData.file_name,
        id: pendingData.id,
        pending: true,
        sharepoint_url: pendingData.sharepoint_url,
      })
    },

    onOneDriveFileValidated (validatedData) {
      const spFiles = this.docketEntry.docket_entry_sharepoint_files
      if (!spFiles) return

      const file = spFiles.find(f => f.id === validatedData.id)
      if (!file) return

      file.fetchStatus = validatedData.fetchStatus
      if (validatedData.fetchStatus === 'fetched') {
        file.file_name = validatedData.file_name
        file.file_size = validatedData.file_size
        file.mime_type = validatedData.mime_type
        file.warning = validatedData.warning
      } else if (validatedData.fetchStatus === 'error') {
        file.error = validatedData.error
      }
    },

    onSharepointFileDeleted (sharepointId) {
      const spFiles = this.docketEntry.docket_entry_sharepoint_files
      if (!spFiles) return

      const index = spFiles.findIndex(f => f.id === sharepointId)
      if (index === -1) return

      const file = spFiles[index]
      if (file.pending) {
        spFiles.splice(index, 1)
        this.$refs.oneDriveInput?.removePendingUrl(file.sharepoint_url)
        return
      }

      const confirmed = window.confirm('Are you sure you want to remove this SharePoint file?')
      if (!confirmed) return

      this.deleteSharepointFile(sharepointId).
        then(_ => {
          spFiles.splice(index, 1)
          this.displayAlert('success', 'SharePoint file removed')
        }).
        catch(_ => {
          this.displayAlert('error', 'Could not remove SharePoint file')
        })
    },

    saveEntry (filesToUpload) {
      this.$emit('on-prepare', filesToUpload)
    },

    submitPendingSharepointFiles (docketEntryId) {
      return this.$refs.oneDriveInput?.submitPendingFiles(docketEntryId) || Promise.resolve()
    },

    updateFileUploadValid (alert) {
      if (alert.alertType && alert.alertMessage) this.displayAlert(alert.alertType, alert.alertMessage)
    },

    updateFilesAreSelected (filesSelected) {
      this.$emit('files-updated', filesSelected)
    },

    ...mapActions(DOCKET_ENTRY_NAMESPACE, [
      'deleteSharepointFile',
    ]),
  },
}
</script>
